import Grid from "@material-ui/core/Grid";
import { Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import * as React from "react";
import gritLogo from "../assets/grit-logo.png";

function stripWhiteBackgroundFromSvgDataUrl(src: string): string {
  const prefix = "data:image/svg+xml;base64,";
  if (typeof src !== "string" || !src.startsWith(prefix)) {
    return src;
  }

  try {
    const base64 = src.slice(prefix.length);
    const svgText = atob(base64);
    const sanitized = svgText
      .replace(/<rect\b[^>]*\bfill=(['"])white\1[^>]*\/>\s*/gi, "")
      .replace(/<rect\b[^>]*\bfill=(['"])#fff(?:fff)?\1[^>]*\/>\s*/gi, "");

    if (sanitized === svgText) {
      return src;
    }

    const encoded = btoa(unescape(encodeURIComponent(sanitized)));
    return `${prefix}${encoded}`;
  } catch {
    return src;
  }
}

const styles = (theme: Theme): Record<string, CSSProperties> => ({
  image: {
    height: 50,
  },
  textCenter: {
    fontFamily: '"Inter", sans-serif !important',
    textAlign: "center",
    fontSize: "21px",
    margin: "15px 0px",
    fontWeight: "bold",
  },
  contents: {
    padding: "1rem",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  screen: {
    maxWidth: "700px",
    paddingBottom: 0,
    paddingTop: 0,
    width: "100%",
  },
  surveyBody: {
    background: "#fff",
    padding: "1rem",
    boxShadow: "none",
    borderRadius: 0,
    borderLeft: "2px solid rgba(225, 229, 239, 0.50)",
    borderRight: "2px solid rgba(225, 229, 239, 0.50)",
    borderBottom: "2px solid rgba(225, 229, 239, 0.50)",
  },
  surveyHeaderSticky: {
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  logoDiv: {
    height: 50,
    maxHeight: 50,
    minHeight: 50,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  topLogo: {},
  footerBlock: {
    paddingTop: "12px",
    background: "inherit",
  },
  surveyHeader: {
    background: "#050505",
    color: "#ffffff",
    borderRadius: "0 0 18px 18px",
    padding: "22px 20px 18px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  surveyHeaderTitle: {
    fontSize: "26px",
    lineHeight: 1.1,
    fontWeight: 800,
    color: "#ffffff",
    margin: "0 0 4px 0",
  },
  surveyHeaderSubtitle: {
    fontSize: "13px",
    lineHeight: 1.4,
    color: "#f4a640",
    fontWeight: 700,
    margin: "0 0 10px 0",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  surveyHeaderBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#f7f7f7",
    fontSize: "11px",
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  surveyProgressBar: {
    flex: 1,
    height: 7,
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
    overflow: "hidden",
  },
  surveyProgressFill: {
    height: "100%",
    borderRadius: 999,
    background: "#9ea6b8",
  },
});

interface IMobilePageProps {
  children: React.ReactNode;
  headerIcon?: React.ReactNode;
  header: string;
  title: string;
  subtitle?: string;
  schoolLogo: string;
  schoolLogo2: string;
  tagline?: string;
  schoolName?: string;
  headLine?: string;
  type?: string;
  forVideoMidSeson?: boolean;
  showSurveyHeader?: boolean;
  showSurveyProgress?: boolean;
  compactSurveyLayout?: boolean;
  tightSurveyHeader?: boolean;
  sportName?: string;
  coachName?: string;
  seasonName?: string;
  progressCurrent?: number;
  progressTotal?: number;
}

type IDirectorPagePropsWithStyles = IMobilePageProps &
  WithStyles<keyof ReturnType<typeof styles>>;

const MobilePage: React.SFC<IDirectorPagePropsWithStyles> = ({
  classes,
  children,
  title,
  schoolLogo,
  schoolLogo2,
  headLine,
  schoolName,
  showSurveyHeader = false,
  showSurveyProgress = true,
  compactSurveyLayout = false,
  tightSurveyHeader = false,
  sportName,
  coachName,
  seasonName,
  progressCurrent,
  progressTotal,
}: IDirectorPagePropsWithStyles) => {
  const [isHeaderCompact, setIsHeaderCompact] = React.useState(false);
  const touchStartYRef = React.useRef<number | undefined>(undefined);
  const categoryLabel =
    headLine && headLine.toLowerCase() === "other"
      ? "Written Response"
      : headLine || "";
  const progressPercent =
    typeof progressCurrent === "number" &&
    typeof progressTotal === "number" &&
    progressTotal > 0
      ? Math.round((progressCurrent / progressTotal) * 100)
      : 0;
  const coachLine =
    sportName && coachName
      ? `${coachName} - ${sportName}`
      : coachName || sportName || title || "Survey";
  // Keep footer identical across respondent pages (same as ChooseCoachPage).
  const footerTopPadding = "8px";
  const footerLogoMargin = "0px";
  const footerCopyMargin = "10px";

  React.useEffect(() => {
    if (!showSurveyHeader || !showSurveyProgress) {
      setIsHeaderCompact(false);
      return;
    }

    const getScrollTop = () =>
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const onScroll = () => {
      setIsHeaderCompact(getScrollTop() > 2);
    };

    const compactOnForwardScroll = () => {
      if (getScrollTop() <= 2) {
        setIsHeaderCompact(true);
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (event.deltaY > 0) {
        compactOnForwardScroll();
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      touchStartYRef.current =
        event.touches.length > 0 ? event.touches[0].clientY : undefined;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (touchStartYRef.current === undefined || event.touches.length === 0) {
        return;
      }

      if (event.touches[0].clientY < touchStartYRef.current - 2) {
        compactOnForwardScroll();
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [showSurveyHeader, showSurveyProgress]);

  return (
    <div className="grid-coach-member">
      <Grid
        style={{ minHeight: "100vh" }}
        container={true}
        direction="column"
        alignItems="center"
      >
        <Grid className={classes.screen}>
          {showSurveyHeader ? (
            <header className={`top-header ${isHeaderCompact ? "top-header--compact" : ""}`}>
              <div className="header-inner">
                <div className="header-main">
                  <div className="header-copy">
                    <h1 className="brand">
                      {schoolName || "School"}
                    </h1>
                    <p className="subtitle">
                      {coachLine}
                    </p>
                  </div>
                  {schoolLogo && (
                    <img
                      src={stripWhiteBackgroundFromSvgDataUrl(schoolLogo)}
                      alt="School Logo"
                      className="logo"
                    />
                  )}
                </div>

                <div
                  className="header-middle"
                    style={
                      compactSurveyLayout
                        ? { marginTop: 8 }
                        : tightSurveyHeader
                        ? { marginTop: 0 }
                        : undefined
                  }
                >
                  <div className="chip-row">
                    <span className="chip">
                      <span className="dot" />
                      <span className="chip-label">Category:</span>
                      <span className="chip-value">{categoryLabel}</span>
                    </span>
                    {seasonName && (
                      <span className="chip">
                        <span className="dot" />
                        <span className="chip-label">Season:</span>
                        <span className="chip-value">{seasonName}</span>
                      </span>
                    )}
                  </div>
                </div>

                {showSurveyProgress && (
                  <div className="progress-wrap">
                    <div className="progress-top">
                      <span className="progress-label">Progress</span>
                    </div>
                    <div className="progress-meter">
                      {progressPercent > 0 && (
                        <div className="progress-pct progress-pct--floating">
                          {progressPercent}%
                        </div>
                      )}
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{ width: `${progressPercent}%` }}
                        />
                        <div
                          className="progress-thumb"
                          aria-hidden="true"
                          style={{ left: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                    {/* <div className="progress-scale">
                      <span>Strongly Disagree</span>
                      <span>Strongly Agree</span>
                    </div> */}
                  </div>
                )}
              </div>
            </header>
          ) : (
            <>
              {schoolLogo && (
                <div className={`${classes.logoDiv} ${classes.topLogo}`}>
                  <img
                    id="target"
                    alt="School Logo"
                    src={stripWhiteBackgroundFromSvgDataUrl(schoolLogo)}
                    className={classes.image}
                  />
                </div>
              )}
              {schoolName && <h2 className={classes.textCenter}>{schoolName}</h2>}
            </>
          )}

          <Grid
            className={`${classes.contents} ${showSurveyHeader ? classes.surveyBody : ""}`}
          >
            {!showSurveyHeader && typeof sportName !== "undefined" && sportName && (
              <h3 className={classes.textCenter}>{sportName}</h3>
            )}
            {!showSurveyHeader && !sportName && title && (
              <h3 className={classes.textCenter}>{title}</h3>
            )}
            {children}
            <div className={classes.footerBlock} style={{ paddingTop: footerTopPadding }}>
              <div className={classes.logoDiv}>
                <div style={{ marginBottom: footerLogoMargin }}>
                  <img
                    src={gritLogo}
                    alt="Grit Logo"
                    style={{
                      border: "none",
                      boxShadow: "none",
                      display: "block",
                      height: 42,
                      objectFit: "contain",
                      outline: "none",
                      width: 42,
                    }}
                  />
                </div>
              </div>
              <div>
                <p
                  className={classes.textCenter}
                  style={{
                    color: "#a0a5b3",
                    fontSize: "12px",
                    fontWeight: "normal",
                    marginTop: footerCopyMargin,
                  }}
                >
                  Copyright © {new Date().getFullYear()} all rights reserved, Grit
                  Leadership<sup>TM</sup> , LLC
                </p>
              </div>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles)(MobilePage);




// WEBPACK FOOTER //
// ./src/components/MobilePage.tsx